import { MenuFoldOutlined } from '@ant-design/icons';
import { Grid, Layout } from 'antd';
import classNames from 'classnames';
import pick from 'lodash/pick';
import PropTypes from 'prop-types';
import React, { memo, useEffect, useMemo } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { useResponsive } from '@/contexts/ResponsiveContext';
import { useSidebar } from '@/contexts/SidebarContext';
import ResponsiveSider from '@/layout/Horizontal/ResponsiveSider';
import MainHeader from '@/layout/MainHeader';
import NavigationMenu from '@/layout/Menu/NavigationMenu';
import { useIsAdminByRoles } from '@/modules/admin/hooks/useUser';
import { layout, LAYOUT_TYPE } from '@/settings';

import style from './index.less';

const { Header } = Layout;
const { useBreakpoint } = Grid;

const SiderBar = styled(Layout.Sider)`
  & > :first-child {
    display: flex;
    ${() => layout.type === LAYOUT_TYPE.HORIZONTAL && 'flex-direction: column;'}
  }
`;

const HorizontalLayout = ({ children, ...props }) => {
  const { lg } = useBreakpoint();
  const { t } = useTranslation('navigation');
  const collapsedWidth = !lg ? 0 : layout.options.collapsedWidth;
  const { collapsed, toggleCollapsed, setCollapsed } = useSidebar();
  const isAdmin = useIsAdminByRoles();
  
  const siderBarOptions = useMemo(() => pick(layout.options, ['width', 'zeroWidthTriggerStyle', 'theme']), []);
  
  const { isMobile } = useResponsive();
  const location = useLocation();
  
  useEffect(() => {
    if (isMobile) {
      setCollapsed(true);
    }
  }, [isMobile, location, setCollapsed]);
  
  const trigger = <div
    className={classNames('sidebar-trigger', { [`position-${layout.options.triggerPosition}`]: !collapsed })}>
    <MenuFoldOutlined
      className={classNames('transition duration-200 ease-in-out trigger-item-1',
        { 'transform rotate-180': collapsed })}/>
    {
      layout.options.useTriggerText &&
      <span
        className={classNames('transition duration-200 ease-in-out m-2 trigger-item-2', { 'hidden': collapsed })}>
        {t('sidebarTrigger')}
      </span>
    }
  </div>;
  
  return (
    <Layout hasSider={isAdmin}>
      <Header>
        <MainHeader isCenter={!isAdmin}/>
      </Header>
      {isAdmin && <div>
        <div
          className={classNames(style.overlayer, { 'hidden': collapsed }, 'lg:hidden')}
          onClick={toggleCollapsed}
        />
        <ResponsiveSider {...layout.options} collapsed={collapsed} className={'hidden lg:block'}/>
        <SiderBar
          id="side-bar-nav" collapsible
          className={classNames(style.sider, { [style.siderCollapsed]: collapsed }, 'fixed h-screen left-0 z-40 bg-primary')}
          collapsed={collapsed}
          onCollapse={setCollapsed}
          collapsedWidth={collapsedWidth}
          trigger={trigger}
          {...siderBarOptions}
        >
          <Scrollbars
            id="scroll-bars"
            // This will activate auto hide
            autoHide
            // Hide delay in ms
            autoHideTimeout={1000}
            // Duration for hide animation in ms.
            autoHideDuration={200}
            style={{
              height: 'calc(100% - 60px)'
            }}
          >
            <div className={classNames({ 'mt-2': collapsed })}>
              <NavigationMenu {...layout.options} {...props} collapsed={collapsed}/>
            </div>
          </Scrollbars>
        </SiderBar>
      </div>}
      {
        children && <Layout className="relative bg-white">
          {isAdmin && <div className={classNames(style.headerSpace)}/>}
          {children}
        </Layout>
      }
    </Layout>
  );
};

export default memo(HorizontalLayout);

HorizontalLayout.propTypes = {
  children: PropTypes.any
};
